import {
  type Env,
  getAccOrigin,
  setEnv,
  setRegion,
} from '@adsk/acs-sc-web-platform-api';

import {
  getExoSkeletonRuntime,
  RuntimeRemoteInfo,
  setIsInExoSkeleton,
} from '../internal';
import { report, REPORT_TYPES } from '../monitoring';
import { isInExoSkeleton } from '../runtime';
import { fetchRemotes } from './fetch';
import { EXO_SKELETON_EVENT_NAMES } from '../consts';
import { safeStringify } from '../safeStringify';
import { wrapWithEventPropertiesPrefix } from '../wrapWithEventPropertiesPrefix';
import {
  registerReactOverrideRuntimePlugin,
  ReactOverridePluginOptions,
  RequiredHostVersions,
} from './exoSharedReactOverridePlugin';
import { registerApiRemoteEntryTimeoutPlugin } from './remoteEntryTimeoutPlugin';

export interface RemoteInfo {
  scope: string;
  url: string;
  moniker?: string;
  isBridge?: boolean;
}

export function getRemoteRuntime(scope: string) {
  const runtime = getExoSkeletonRuntime();
  return runtime?.remotes?.[scope];
}

export function setExoSkeletonRemotes(
  remotes: Record<string, RuntimeRemoteInfo>,
) {
  const runtime = getExoSkeletonRuntime();

  runtime.remotes = remotes;
}

type PartialRemoteRuntime = Partial<RuntimeRemoteInfo> | null | undefined;

interface CreateRuntimeRemotesOptions {
  getRemoteOverride?(
    scope: string,
  ): Promise<PartialRemoteRuntime> | PartialRemoteRuntime;
  transformRemotesUrl?: (url: string) => string;
  transformRemoteEntryUrl?: (url: string, scope: string) => string;
}

async function createRuntimeRemotes({
  getRemoteOverride,
  transformRemotesUrl,
  transformRemoteEntryUrl,
}: CreateRuntimeRemotesOptions): Promise<Record<string, RuntimeRemoteInfo>> {
  const remotes = await fetchRemotes({ transformRemotesUrl });
  const runtimeRemotes: Record<string, RuntimeRemoteInfo> = {};

  for (const { scope, url, moniker, isBridge } of remotes) {
    const overrides = (await getRemoteOverride?.(scope)) || {};
    const hasOverride = Object.keys(overrides).length > 0;

    const resolvedMoniker = overrides.moniker || moniker;
    let resolvedUrlStr = overrides.url;
    if (!resolvedUrlStr) {
      resolvedUrlStr = transformRemoteEntryUrl?.(url, scope) || url;
    }

    const resolvedUrl = new URL(resolvedUrlStr, getAccOrigin());

    runtimeRemotes[scope] = {
      override: hasOverride,
      moniker: resolvedMoniker,
      url: resolvedUrl.href,
      scope,
      isBridge,
      /**
       * @deprecated kept for backwards compatibility
       */
      get: async () => {
        return {
          scope,
          url: resolvedUrl.href,
        };
      },
    };
  }

  return runtimeRemotes;
}

interface EnvironmentConfig {
  env?: Env;
  region?: string;
}

export function getAllRemotesRuntime() {
  const runtime = getExoSkeletonRuntime();
  return runtime?.remotes || {};
}

function setEnvAndRegion({ env, region }: EnvironmentConfig, hostName: string) {
  if (env) {
    setEnv(env);

    const context = {
      env,
    };

    report({
      type: REPORT_TYPES.info,
      value: 'EXO_SKELETON_ENVIRONMENT_CONFIG_SET_ENV',
      scope: hostName,
      context: {
        ...context,
        ...wrapWithEventPropertiesPrefix({
          data: safeStringify(context),
          message: 'EXO_SKELETON_ENVIRONMENT_CONFIG_SET_ENV',
          type: REPORT_TYPES.info,
        }),
      },
    });
  }

  if (region) {
    setRegion(region as never);

    const context = {
      region,
    };

    report({
      type: REPORT_TYPES.info,
      value: 'EXO_SKELETON_ENVIRONMENT_CONFIG_SET_REGION',
      scope: hostName,
      context: {
        ...context,
        ...wrapWithEventPropertiesPrefix({
          data: safeStringify(context),
          message: 'EXO_SKELETON_ENVIRONMENT_CONFIG_SET_REGION',
          type: REPORT_TYPES.info,
        }),
      },
    });
  }
}

export interface InitializeExoSkeletonRuntimeValuesOptions extends CreateRuntimeRemotesOptions {
  environmentConfig?: EnvironmentConfig;
  hostName?: string;
  overrideReact?: {
    requiredHostVersions: RequiredHostVersions;
    availableReactScopes?: ReactOverridePluginOptions['availableReactScopes'];
  };
}

/**
 * Initializes the ExoSkeleton runtime values
 * @param getRemoteOverride - A function that returns the remote override for a given scope
 * @param hostName - The name of the host application e.g. ACCExoSkeleton
 * @param environmentConfig - The environment configuration
 * @param transformRemotesUrl - A function that transforms the remotes url (where to fetch remotes.json)
 * @param transformRemoteEntryUrl - A function that transforms individual remote entry URLs from remotes.json.
 *   Receives the original URL and scope name. Applied before getRemoteOverride, so explicit overrides take precedence.
 * @param overrideReact - When set (with `requiredHostVersions` and `hostName`), registers the React override runtime plugin. `hostName` is required whenever `overrideReact` is used.
 *
 * `overrideReact` registers a global runtime plugin so remotes resolve the host's shared `react` / `react-dom` (default share scope), which matters when the host and remotes disagree on React versions (including sync remote flows). `requiredHostVersions` must match the `requiredVersion` values in the host's Module Federation shared config for those packages.
 *
 * Optional `overrideReact.availableReactScopes` lists additional share scope names (e.g. `react18`) where the plugin should **not** force `shareScopeMap.default`, so versioned scopes keep native resolution. The scope name `default` is ignored if passed in that list.
 */
export async function initializeExoSkeletonRuntimeValues({
  getRemoteOverride,
  hostName,
  environmentConfig: { env, region } = {},
  transformRemotesUrl,
  transformRemoteEntryUrl,
  overrideReact,
}: InitializeExoSkeletonRuntimeValuesOptions = {}) {
  if (isInExoSkeleton()) {
    return;
  }

  registerApiRemoteEntryTimeoutPlugin();

  const { requiredHostVersions, availableReactScopes } = overrideReact ?? {};

  if (requiredHostVersions && hostName) {
    registerReactOverrideRuntimePlugin(
      hostName,
      requiredHostVersions,
      availableReactScopes ? { availableReactScopes } : undefined,
    );
  }

  setEnvAndRegion({ env, region }, hostName || 'ACCExoSkeleton');
  setExoSkeletonRemotes(
    await createRuntimeRemotes({
      transformRemotesUrl,
      transformRemoteEntryUrl,
      getRemoteOverride,
    }),
  );

  setIsInExoSkeleton();

  globalThis.dispatchEvent(
    new Event(EXO_SKELETON_EVENT_NAMES.REMOTES_LIST_FETCHED),
  );
}
